/**
 * Groups features and roles by app to avoid repetition in the UI
 * @param {Object} userGroup - User group object with apps array
 * @returns {Array} - Array of grouped app objects
 */
export const groupRolesByApp = userGroup => {
  if (!userGroup?.apps || userGroup.apps.length === 0) {
    return [];
  }

  const appMap = new Map();

  userGroup.apps.forEach(app => {
    const key = `${app.installationTag}_${app.communityTag}`;

    if (!appMap.has(key)) {
      appMap.set(key, {
        installationTag: app.installationTag,
        installationName: app.alias || app.installationName,
        communityTag: app?.community?.name || app?.community?.tag,
        features: [],
      });
    }

    if (app.features && app.features.length > 0) {
      const existingApp = appMap.get(key);
      existingApp.features.push(...app.features);
    }
  });

  return Array.from(appMap.values());
};

/**
 * Counts total number of roles in a user group
 * @param {Object} userGroup - User group object
 * @returns {number} - Total role count
 */
export const countTotalRoles = userGroup => {
  if (!userGroup?.apps) return 0;

  return userGroup.apps.reduce((total, app) => {
    if (!app.features) return total;
    return (
      total +
      app.features.reduce((featureTotal, feature) => {
        return featureTotal + (feature.roles?.length || 0);
      }, 0)
    );
  }, 0);
};

/**
 * Counts total number of unique apps in a user group
 * @param {Object} userGroup - User group object
 * @returns {number} - Total unique app count
 */
export const countTotalApps = userGroup => {
  if (!userGroup?.apps) return 0;

  const uniqueApps = new Set(
    userGroup.apps.map(app => `${app.installationTag}_${app.communityTag}`)
  );

  return uniqueApps.size;
};

/**
 * Checks if a user group has any roles assigned
 * @param {Object} userGroup - User group object
 * @returns {boolean} - True if user group has roles
 */
export const hasRoles = userGroup => {
  return (
    userGroup?.apps &&
    userGroup.apps.length > 0 &&
    userGroup.apps.some(app => app.features && app.features.length > 0)
  );
};
